/**
 * Voice AI — STT/TTS Settings Sub-Component
 * Two sections: STT providers + TTS providers, with shared edit form.
 * Matches the settings-panel design (same CSS as llm-settings.js).
 * Thin rendering only — all API calls go through speech-service.js.
 */

import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { settingsStyles } from '../../styles/settings-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { speechService } from '../../services/speech-service.js';
import { confirmService } from '../../services/confirm-service.js';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'it', label: 'Italian' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'nl', label: 'Dutch' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'zh', label: 'Chinese' },
];

export class SttTtsSettings extends LitElement {
  static properties = {
    sttProviders: { type: Array },
    ttsProviders: { type: Array },
    _editingType: { state: true },    // 'stt' | 'tts' | null
    _editing: { state: true },        // null | 'new' | key suffix
    _form: { state: true },
    _saving: { state: true },
    _deleting: { state: true },
    _showApiKey: { state: true },
  };

  static styles = [designSystem, componentStyles, settingsStyles];

  constructor() {
    super();
    this.sttProviders = [];
    this.ttsProviders = [];
    this._editingType = null;
    this._editing = null;
    this._form = {};
    this._saving = false;
    this._deleting = false;
    this._showApiKey = false;
  }

  render() {
    return html`
      ${iconStyles}
      <div class="content-header">
        <h2 class="content-title">Speech Configuration</h2>
        <p class="content-description">Configure speech-to-text and text-to-speech providers for voice AI conversations.</p>
      </div>

      <!-- STT Section -->
      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid fa-microphone section-icon"></i>
          <span class="section-title">Speech-to-Text (STT) Providers</span>
        </div>
        <p class="section-description">Configure STT providers for transcribing caller speech during voice calls.</p>

        ${this.sttProviders.length > 0 ? html`
          <div class="provider-list">
            ${this.sttProviders.map(p => this._renderProviderItem(p, 'stt'))}
          </div>
        ` : html`
          <div class="empty-hint">
            <i class="fa-sharp fa-light fa-microphone"></i>
            No STT providers configured yet. Add your first speech-to-text provider below.
          </div>
        `}

        <div class="add-btn" @click=${() => this._startAdd('stt')}>
          <i class="fa-sharp fa-light fa-plus"></i>
          Add STT Provider
        </div>
      </div>

      <!-- TTS Section -->
      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid fa-volume-high section-icon"></i>
          <span class="section-title">Text-to-Speech (TTS) Providers</span>
        </div>
        <p class="section-description">Configure TTS providers for AI voice output during calls.</p>

        ${this.ttsProviders.length > 0 ? html`
          <div class="provider-list">
            ${this.ttsProviders.map(p => this._renderProviderItem(p, 'tts'))}
          </div>
        ` : html`
          <div class="empty-hint">
            <i class="fa-sharp fa-light fa-volume-high"></i>
            No TTS providers configured yet. Add your first text-to-speech provider below.
          </div>
        `}

        <div class="add-btn" @click=${() => this._startAdd('tts')}>
          <i class="fa-sharp fa-light fa-plus"></i>
          Add TTS Provider
        </div>
      </div>

      <!-- Edit / Add Form -->
      ${this._editing !== null ? this._renderEditSection() : ''}
    `;
  }

  _renderProviderItem(provider, type) {
    const def = type === 'stt'
      ? speechService.getSttProviderDef(provider.provider)
      : speechService.getTtsProviderDef(provider.provider);
    const icon = def ? def.icon : 'fa-code';
    const providerName = def ? def.name : provider.provider;
    const isActive = this._editingType === type && this._editing === provider.key;

    // Build meta string: model, plus voice for TTS
    let meta = provider.model || '';
    if (type === 'tts' && provider.voice) {
      meta += ' \u00B7 ' + provider.voice;
    }
    if (type === 'stt' && provider.language) {
      const lang = LANGUAGES.find(l => l.code === provider.language);
      meta += ' \u00B7 ' + (lang ? lang.label : provider.language);
    }
    if (provider.region) {
      meta += ' \u00B7 ' + provider.region;
    }

    return html`
      <div class="provider-item ${isActive ? 'active' : ''}" @click=${() => this._startEdit(provider, type)}>
        <div class="provider-info">
          <div class="provider-icon">
            <i class="fa-sharp-duotone fa-solid ${icon}"></i>
          </div>
          <div class="provider-details">
            <div class="provider-name">
              ${provider.label}
              <span style="color:var(--color-text-tertiary);font-size:var(--text-xs);font-weight:400;">${providerName}</span>
            </div>
            <div class="provider-meta">${meta}</div>
          </div>
        </div>
        <div class="provider-right">
          ${provider.api_key_status === 'configured'
            ? html`<span class="badge badge-success">Key Set</span>`
            : html`<span class="badge badge-warning">No Key</span>`
          }
          ${provider.is_default ? html`<span class="badge badge-primary">Default</span>` : ''}
          <i class="fa-sharp fa-light fa-pen edit-icon"></i>
        </div>
      </div>
    `;
  }

  _renderEditSection() {
    const isNew = this._editing === 'new';
    const isStt = this._editingType === 'stt';
    const defs = isStt
      ? speechService.getSttProviderDefinitions()
      : speechService.getTtsProviderDefinitions();
    const selectedDef = defs.find(d => d.id === this._form.provider) || defs[0];
    const models = selectedDef.models || [];
    const isCustom = selectedDef.id === 'custom';

    // TTS voices
    const voices = !isStt ? (selectedDef.voices || []) : [];
    const hasVoiceSelect = voices.length > 0;

    const typeLabel = isStt ? 'STT' : 'TTS';
    const sectionIcon = isStt ? 'fa-microphone' : 'fa-volume-high';

    return html`
      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid ${isNew ? 'fa-plus-circle' : 'fa-pen-to-square'} section-icon"></i>
          <span class="section-title">${isNew ? `Add New ${typeLabel} Provider` : `Edit ${typeLabel} Provider`}</span>
        </div>
        <p class="section-description">${isNew
          ? `Configure a new ${isStt ? 'speech-to-text' : 'text-to-speech'} provider with API credentials.`
          : `Update the configuration for this ${typeLabel} provider.`
        }</p>

        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Provider</label>
            <select class="form-select"
              .value=${this._form.provider || ''}
              @change=${e => this._onProviderChange(e.target.value)}
              ?disabled=${!isNew}
            >
              ${defs.map(d => html`<option value=${d.id} ?selected=${d.id === this._form.provider}>${d.name}</option>`)}
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Label</label>
            <input class="form-input" type="text"
              placeholder="e.g. ${isStt ? 'Deepgram Nova' : 'OpenAI TTS'}"
              .value=${this._form.label || ''}
              @input=${e => this._form = { ...this._form, label: e.target.value }}
            />
            <div class="form-hint">Friendly name to identify this provider.</div>
          </div>
        </div>

        <div class="form-grid full" style="margin-top:var(--space-4);">
          <div class="form-group">
            <label class="form-label">API Key</label>
            <div class="password-wrap">
              <input class="form-input"
                type="${this._showApiKey ? 'text' : 'password'}"
                placeholder=${selectedDef.placeholder || 'Enter API key'}
                .value=${this._form.api_key || ''}
                @input=${e => this._form = { ...this._form, api_key: e.target.value }}
              />
              <button class="password-toggle" @click=${() => this._showApiKey = !this._showApiKey}>
                <i class="fa-sharp fa-light ${this._showApiKey ? 'fa-eye-slash' : 'fa-eye'}"></i>
              </button>
            </div>
            ${!isNew && this._form._masked ? html`
              <div class="form-hint">Current key: ${this._form._masked}. Leave blank to keep existing key.</div>
            ` : html`
              <div class="form-hint">Your API key. Keep this secret.</div>
            `}
          </div>
        </div>

        ${isCustom ? html`
          <div class="form-grid full" style="margin-top:var(--space-4);">
            <div class="form-group">
              <label class="form-label">Base URL</label>
              <input class="form-input" type="text"
                placeholder="https://api.example.com/v1"
                .value=${this._form.base_url || ''}
                @input=${e => this._form = { ...this._form, base_url: e.target.value }}
              />
              <div class="form-hint">Custom API endpoint URL.</div>
            </div>
          </div>
        ` : ''}

        ${selectedDef.hasRegion ? html`
          <div class="form-grid full" style="margin-top:var(--space-4);">
            <div class="form-group">
              <label class="form-label">Region</label>
              <select class="form-select"
                .value=${this._form.region || 'eastus'}
                @change=${e => this._form = { ...this._form, region: e.target.value }}
              >
                <option value="eastus" ?selected=${'eastus' === this._form.region}>East US</option>
                <option value="eastus2" ?selected=${'eastus2' === this._form.region}>East US 2</option>
                <option value="westus" ?selected=${'westus' === this._form.region}>West US</option>
                <option value="westus2" ?selected=${'westus2' === this._form.region}>West US 2</option>
                <option value="centralus" ?selected=${'centralus' === this._form.region}>Central US</option>
                <option value="northeurope" ?selected=${'northeurope' === this._form.region}>North Europe</option>
                <option value="westeurope" ?selected=${'westeurope' === this._form.region}>West Europe</option>
                <option value="southeastasia" ?selected=${'southeastasia' === this._form.region}>Southeast Asia</option>
                <option value="eastasia" ?selected=${'eastasia' === this._form.region}>East Asia</option>
                <option value="australiaeast" ?selected=${'australiaeast' === this._form.region}>Australia East</option>
              </select>
              <div class="form-hint">Azure region where your Speech resource is deployed.</div>
            </div>
          </div>
        ` : ''}

        <div class="form-grid" style="margin-top:var(--space-4);">
          <div class="form-group">
            <label class="form-label">Model</label>
            ${isCustom ? html`
              <input class="form-input" type="text"
                placeholder="model-name"
                .value=${this._form.model || ''}
                @input=${e => this._form = { ...this._form, model: e.target.value }}
              />
            ` : html`
              <select class="form-select"
                .value=${this._form.model || ''}
                @change=${e => this._form = { ...this._form, model: e.target.value }}
              >
                ${models.map(m => html`<option value=${m} ?selected=${m === this._form.model}>${m}</option>`)}
              </select>
            `}
            <div class="form-hint">Model to use for ${isStt ? 'transcription' : 'speech synthesis'}.</div>
          </div>

          ${isStt ? html`
            <!-- STT: Language -->
            <div class="form-group">
              <label class="form-label">Language</label>
              <select class="form-select"
                .value=${this._form.language || 'en'}
                @change=${e => this._form = { ...this._form, language: e.target.value }}
              >
                ${LANGUAGES.map(l => html`<option value=${l.code} ?selected=${l.code === this._form.language}>${l.label}</option>`)}
              </select>
              <div class="form-hint">Primary language for transcription.</div>
            </div>
          ` : html`
            <!-- TTS: Voice -->
            <div class="form-group">
              <label class="form-label">Voice</label>
              ${hasVoiceSelect ? html`
                <select class="form-select"
                  .value=${this._form.voice || ''}
                  @change=${e => this._form = { ...this._form, voice: e.target.value }}
                >
                  ${voices.map(v => html`<option value=${v} ?selected=${v === this._form.voice}>${v}</option>`)}
                </select>
              ` : html`
                <input class="form-input" type="text"
                  placeholder="${isCustom ? 'voice-id' : (selectedDef.defaultVoice || 'Voice ID or name')}"
                  .value=${this._form.voice || ''}
                  @input=${e => this._form = { ...this._form, voice: e.target.value }}
                />
              `}
              <div class="form-hint">${hasVoiceSelect ? 'Select a voice for speech output.' : (selectedDef.defaultVoiceLabel ? `Enter voice UUID. Default: ${selectedDef.defaultVoiceLabel}` : 'Enter voice ID or name from provider.')}</div>
            </div>
          `}
        </div>

        ${!isStt ? html`
          <!-- TTS: Speed slider -->
          <div class="form-grid full" style="margin-top:var(--space-4);">
            <div class="form-group">
              <div class="slider-wrap">
                <div class="slider-header">
                  <label class="form-label">Speed</label>
                  <span class="slider-value">${(this._form.speed ?? 1.0).toFixed(1)}x</span>
                </div>
                <input type="range" min="0.5" max="2.0" step="0.1"
                  .value=${String(this._form.speed ?? 1.0)}
                  @input=${e => this._form = { ...this._form, speed: parseFloat(e.target.value) }}
                />
                <div class="form-hint">Speech speed multiplier. 1.0 is normal, lower is slower, higher is faster.</div>
              </div>
            </div>
          </div>
        ` : ''}

        <div class="form-actions">
          <button class="btn btn-primary" @click=${this._handleSave} ?disabled=${this._saving}>
            <i class="fa-sharp fa-light ${this._saving ? 'fa-spinner-third fa-spin' : 'fa-check'}"></i>
            ${this._saving ? 'Saving...' : `Save ${typeLabel} Provider`}
          </button>
          <button class="btn btn-secondary" @click=${this._cancelEdit}>
            Cancel
          </button>
          ${!isNew ? html`
            <span class="spacer"></span>
            ${!this._getEditingProvider()?.is_default ? html`
              <button class="btn btn-default" @click=${this._handleSetDefault}>
                <i class="fa-sharp fa-light fa-star"></i>
                Set as Default
              </button>
            ` : ''}
            <button class="btn btn-danger" @click=${this._handleDelete} ?disabled=${this._deleting}>
              <i class="fa-sharp fa-light ${this._deleting ? 'fa-spinner-third fa-spin' : 'fa-trash-can'}"></i>
              Delete
            </button>
          ` : ''}
        </div>
      </div>
    `;
  }

  /* ─── Actions ─── */

  _startAdd(type) {
    const defs = type === 'stt'
      ? speechService.getSttProviderDefinitions()
      : speechService.getTtsProviderDefinitions();
    this._editingType = type;
    this._editing = 'new';
    this._showApiKey = false;

    const form = {
      provider: defs[0].id,
      label: '',
      api_key: '',
      model: defs[0].models[0] || '',
      base_url: '',
      region: ''
    };

    if (type === 'stt') {
      form.language = 'en';
    } else {
      const voices = defs[0].voices || [];
      form.voice = voices[0] || defs[0].defaultVoice || '';
      form.speed = 1.0;
    }

    this._form = form;
  }

  _startEdit(provider, type) {
    if (this._editingType === type && this._editing === provider.key) {
      this._editingType = null;
      this._editing = null;
      return;
    }
    this._editingType = type;
    this._editing = provider.key;
    this._showApiKey = false;

    const form = {
      provider: provider.provider,
      label: provider.label,
      api_key: '',
      model: provider.model,
      base_url: provider.base_url || '',
      region: provider.region || '',
      _masked: provider.api_key_masked || ''
    };

    if (type === 'stt') {
      form.language = provider.language || 'en';
    } else {
      form.voice = provider.voice || '';
      form.speed = provider.speed ?? 1.0;
    }

    this._form = form;
  }

  _cancelEdit() {
    this._editingType = null;
    this._editing = null;
    this._form = {};
  }

  _onProviderChange(providerId) {
    const isStt = this._editingType === 'stt';
    const defs = isStt
      ? speechService.getSttProviderDefinitions()
      : speechService.getTtsProviderDefinitions();
    const def = defs.find(d => d.id === providerId) || defs[0];
    const models = def.models || [];

    const updates = {
      ...this._form,
      provider: providerId,
      model: models[0] || ''
    };

    if (!isStt) {
      const voices = def.voices || [];
      updates.voice = voices[0] || def.defaultVoice || '';
    }

    this._form = updates;
  }

  _getEditingProvider() {
    if (!this._editing || this._editing === 'new') return null;
    const list = this._editingType === 'stt' ? this.sttProviders : this.ttsProviders;
    return list.find(p => p.key === this._editing);
  }

  async _handleSave() {
    if (this._saving) return;

    const { provider, label, api_key, model } = this._form;
    const isNew = this._editing === 'new';
    const isStt = this._editingType === 'stt';
    const typeLabel = isStt ? 'STT' : 'TTS';

    if (!provider || !label || !model) {
      this._dispatch('toast', { message: 'Provider, label, and model are required', type: 'error' });
      return;
    }
    if (isNew && !api_key) {
      this._dispatch('toast', { message: 'API key is required for new providers', type: 'error' });
      return;
    }

    this._saving = true;
    try {
      const payload = { provider, label, model };
      if (api_key) payload.api_key = api_key;
      if (this._form.base_url) payload.base_url = this._form.base_url;
      if (this._form.region) payload.region = this._form.region;

      if (isStt) {
        payload.language = this._form.language || 'en';
      } else {
        payload.voice = this._form.voice || '';
        payload.speed = this._form.speed ?? 1.0;
      }

      let result;
      if (isNew) {
        result = isStt
          ? await speechService.createSttProvider(payload)
          : await speechService.createTtsProvider(payload);
      } else {
        result = isStt
          ? await speechService.updateSttProvider(this._editing, payload)
          : await speechService.updateTtsProvider(this._editing, payload);
      }

      if (result.success) {
        const savedKey = result.key || this._editing;
        this._editingType = null;
        this._editing = null;
        this._form = {};
        this._dispatch(isStt ? 'stt-saved' : 'tts-saved', { key: savedKey });
        this._dispatch('toast', { message: `${typeLabel} provider ${isNew ? 'added' : 'updated'}` });
      } else {
        this._dispatch('toast', { message: result.error || 'Save failed', type: 'error' });
      }
    } catch (err) {
      this._dispatch('toast', { message: `Network error saving ${typeLabel} provider`, type: 'error' });
    } finally {
      this._saving = false;
    }
  }

  async _handleDelete() {
    if (this._deleting || this._editing === 'new') return;
    const isStt = this._editingType === 'stt';
    const typeLabel = isStt ? 'STT' : 'TTS';
    const label = this._form.label || 'this provider';
    const key = this._editing;

    const confirmed = await confirmService.show({
      title: `Delete ${typeLabel} Provider`,
      message: `Delete "${label}"?`,
      description: 'This cannot be undone. Any agents using this provider will fall back to the default.',
    });
    if (!confirmed) return;

    this._deleting = true;
    try {
      const result = isStt
        ? await speechService.deleteSttProvider(key)
        : await speechService.deleteTtsProvider(key);

      if (result.success) {
        this._editingType = null;
        this._editing = null;
        this._form = {};
        this._dispatch(isStt ? 'stt-deleted' : 'tts-deleted', { key });
        this._dispatch('toast', { message: `${typeLabel} provider deleted` });
      } else {
        this._dispatch('toast', { message: result.error || 'Delete failed', type: 'error' });
      }
    } catch (err) {
      this._dispatch('toast', { message: `Network error deleting ${typeLabel} provider`, type: 'error' });
    } finally {
      this._deleting = false;
    }
  }

  async _handleSetDefault() {
    if (this._editing === 'new') return;
    const isStt = this._editingType === 'stt';
    const typeLabel = isStt ? 'STT' : 'TTS';

    try {
      const result = isStt
        ? await speechService.setSttDefault(this._editing)
        : await speechService.setTtsDefault(this._editing);

      if (result.success) {
        this._dispatch(isStt ? 'stt-default-changed' : 'tts-default-changed', { key: this._editing });
        this._dispatch('toast', { message: `Default ${typeLabel} provider updated` });
      } else {
        this._dispatch('toast', { message: result.error || 'Failed to set default', type: 'error' });
      }
    } catch (err) {
      this._dispatch('toast', { message: 'Network error', type: 'error' });
    }
  }

  _dispatch(event, detail = {}) {
    this.dispatchEvent(new CustomEvent(event, { detail, bubbles: true, composed: true }));
  }
}

customElements.define('stt-tts-settings', SttTtsSettings);
